// src/components/SiteHeader.jsx
import React, { useEffect, useState, useRef, memo } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Globe2,
  X,
  Menu,
  Check,
  Home,
  BookOpen,
  FileText,
  ChevronDown,
  Newspaper,
  Gamepad2,
  Camera,
  Archive, // ✅ เพิ่มไอคอนสำหรับมรดกความทรงจำ
  Landmark, // ✅ เพิ่มไอคอนสำหรับมรดกโลก
} from "lucide-react";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";

/* ===== Lock scroll เมื่อเมนูเปิด (mobile drawer) ===== */
function useLockBody(lock) {
  useEffect(() => {
    if (!lock) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lock]);
}

// โหลดฟอนต์จีนเฉพาะตอนจำเป็น
function ensureChineseFontLoaded() {
  if (typeof document === "undefined") return;
  if (document.getElementById("font-zh")) return;
  const link = document.createElement("link");
  link.id = "font-zh";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;600;700&display=swap";
  link.onload = () => {
    const ui = getComputedStyle(document.documentElement).getPropertyValue(
      "--font-ui"
    );
    document.documentElement.style.setProperty(
      "--font-zh",
      `"Noto Sans SC", ${ui}`
    );
  };
  document.head.appendChild(link);
}

/* รายการภาษา */
const LANGS = [
  { code: "th", labelKey: "language.th" },
  { code: "en", labelKey: "language.en" },
  { code: "fr", labelKey: "language.fr" },
  { code: "zh", labelKey: "language.zh" },
  { code: "ko", labelKey: "language.ko" },
  { code: "ja", labelKey: "language.ja" },
  { code: "lo", labelKey: "language.lo" },
  { code: "fil", labelKey: "language.fil" },
];

/* map ขนาดตัวอักษร → root font-size */
const FONT_SCALES = {
  small: "93.75%", // ~15px
  base: "100%", // 16px
  large: "112.5%", // ~18px
};

function SiteHeaderInner({ centerSlot = null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false); // mobile drawer
  const [scrolled, setScrolled] = useState(false);

  const [lang, setLang] = useState(i18n.language || "en");
  const [fontScale, setFontScale] = useState(
    (typeof window !== "undefined" && localStorage.getItem("fontScale")) ||
    "base"
  );

  const [langOpen, setLangOpen] = useState(false);
  const langBtnRef = useRef(null);

  useLockBody(open);

  // scroll listener: อัปเดต state เฉพาะตอนที่ค่าเปลี่ยนจริง ๆ
  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > 10;
      setScrolled((prev) => (prev === next ? prev : next));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const applyFontScale = (key) => {
    const val = FONT_SCALES[key] || FONT_SCALES.base;
    setFontScale(key);
    if (typeof window !== "undefined") {
      localStorage.setItem("fontScale", key);
    }
    document.documentElement.style.fontSize = val;
  };

  useEffect(() => {
    const savedLang =
      typeof window !== "undefined" ? localStorage.getItem("lang") : null;
    const useLang = savedLang || i18n.language || "en";
    setLang(useLang);
    document.documentElement.setAttribute("lang", useLang);
    i18n.changeLanguage(useLang).catch(() => { });
    if (useLang === "zh") ensureChineseFontLoaded();

    const savedScale =
      (typeof window !== "undefined" && localStorage.getItem("fontScale")) ||
      "base";
    const scaleKey = FONT_SCALES[savedScale] ? savedScale : "base";
    applyFontScale(scaleKey);
  }, []);

  // ปิด dropdown ภาษาเมื่อคลิกรอบนอก (desktop)
  useEffect(() => {
    function handleOutsideClick(e) {
      if (!langBtnRef.current) return;
      if (!langBtnRef.current.contains(e.target)) setLangOpen(false);
    }
    if (langOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [langOpen]);

  const handleChangeLang = async (code) => {
    setLang(code);
    if (typeof window !== "undefined") localStorage.setItem("lang", code);
    document.documentElement.setAttribute("lang", code);
    try {
      await i18n.changeLanguage(code);
    } catch { }
    if (code === "zh") ensureChineseFontLoaded();
    setLangOpen(false);
  };

  const currentLangItem = LANGS.find((l) => l.code === lang);

  const LangLabel = currentLangItem?.labelKey
    ? t(currentLangItem.labelKey, lang.toUpperCase())
    : lang.toUpperCase();

  return (
    <>
      {/* Header */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "h-16 md:h-20" : "h-20 md:h-24"
          }`}
      >
        <div
          className={`
            w-full px-4 sm:px-6 lg:px-10 h-full flex items-center
            bg-white shadow-md border-b border-black/5 transition-all
          `}
        >
          {/* ซ้าย: โลโก้ + ชื่อแบรนด์ */}
          <Link to="/" className="flex items-center shrink-0 group relative z-50">
            {/* กล่องตัวยึดพื้นที่ (Placeholder) 
    - ขยาย w-36 ถึง w-40 เพื่อกันไม่ให้ข้อความไปทับกับโลโก้ที่ใหญ่ขึ้น
  */}
            <div className={`relative transition-all duration-300 flex items-center ${scrolled ? "w-10 h-10 md:w-12 md:h-12" : "w-16 h-16 md:w-40 md:h-24"
              }`}>

              {/* โลโก้ */}
              <img
                src="/assets/logo.webp"
                alt={t("brand.logoAlt", "โลโก้ Culture Reader @NL.CNX")}
                className={`absolute left-0 z-50 transition-all duration-300 drop-shadow-md group-hover:drop-shadow-xl group-hover:scale-105 ${scrolled
                    ? "top-1/2 -translate-y-1/2 w-10 md:w-12 h-10 md:h-12 origin-center" // เวลาเลื่อนจอ: หดกลับมาพอดีแถบและอยู่ตรงกลาง
                    : "top-1 md:top-3 w-16 md:w-40 h-16 md:h-40 origin-top-left" // ตอนอยู่บนสุด: ขยับลงมาจากขอบบน 12px (top-3) และขยายใหญ่ 160px (w-40)
                  }`}
              />
            </div>

            {/* ข้อความแบรนด์ */}
            <span className="hidden sm:flex flex-col justify-center transition-all duration-300 ml-3 md:ml-4">
              <span className={`font-semibold tracking-tight text-[#5b4a3e] transition-all duration-300 ${scrolled ? "text-base md:text-lg" : "text-lg md:text-xl"
                }`}>
                {t("brand.name", "Culture Reader @NL.CNX")}
              </span>
              <p className={`font-normal opacity-80 transition-all duration-300 ${scrolled ? "text-xs" : "text-sm"
                }`}>
                {t("brand.tagline", "รักอ่าน รักษ์วัฒนธรรม")}
              </p>
            </span>
            <span className="sr-only">{t("brand.homeSr", "กลับสู่หน้าแรก")}</span>
          </Link>

          {/* กลาง: centerSlot (search) เฉพาะ lg ขึ้นไป */}
          <div className="hidden lg:flex flex-1 min-w-0 px-8">
            <div className="w-full max-w-3xl mx-auto">{centerSlot}</div>
          </div>

          {/* ขวา: เมนู (desktop) */}
          <nav className="hidden lg:flex items-center gap-2">
            {/* ลำดับที่ 1: หน้าแรก */}
            <NavLink
              to="/"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <Home className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.home", "หน้าแรก")}
              </span>
            </NavLink>

            {/* ลำดับที่ 2: ทรัพยากรสารสนเทศ */}
            <NavLink
              to="/books"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <BookOpen className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.books", "ทรัพยากรสารสนเทศ")}
              </span>
            </NavLink>

            {/* ลำดับที่ 3: มรดกความทรงจำ แห่งโลก (แบ่ง 2 บรรทัด) */}
            <NavLink
              to="/memory-of-the-world"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <Archive className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium leading-tight flex flex-col">
                <span>{t("nav.mow_1", "มรดกความทรงจำ")}</span>
                <span>{t("nav.mow_2", "แห่งโลก")}</span>
              </span>
            </NavLink>

            {/* ลำดับที่ 4: มรดกโลก */}
            <NavLink
              to="/world-heritage"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <Landmark className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.world_heritage", "มรดกโลกเชียงใหม่")}
              </span>
            </NavLink>

            {/* ลำดับที่ 5: องค์ความรู้ */}
            <NavLink
              to="/articles"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <FileText className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.articles", "องค์ความรู้")}
              </span>
            </NavLink>

            {/* ลำดับที่ 6: เกมล้านนา */}
            <NavLink
              to="/games"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <Gamepad2 className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.games", "เกมล้านนา")}
              </span>
            </NavLink>

            {/* ลำดับที่ 7: ข่าว */}
            <NavLink
              to="/facebook/activities"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-xl transition ${isActive
                  ? "text-[#d8653b] bg-white/70 ring-1 ring-black/10"
                  : "text-[#5b4a3e] hover:text-[#d8653b] hover:bg-white/60"
                }`
              }
            >
              <Newspaper className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">
                {t("nav.facebook", "ข่าว")}
              </span>
            </NavLink>

            {/* Language dropdown (desktop) */}
            <div className="relative" ref={langBtnRef}>
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/90 backdrop-blur-md shadow ring-1 ring-black/10 text-[#5b4a3e] hover:bg-white"
                aria-haspopup="menu"
                aria-expanded={langOpen}
              >
                <Globe2 className="w-5 h-5" />
                <span className="text-sm font-semibold">{LangLabel}</span>
                <ChevronDown className="w-4 h-4 opacity-70" />
              </button>

              {langOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 rounded-xl bg-white shadow-lg ring-1 ring-black/10 p-1 z-50"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => handleChangeLang(l.code)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 ${lang === l.code
                        ? "text-[#d8653b] font-semibold"
                        : "text-[#5b4a3e]"
                        }`}
                    >
                      <span>{t(l.labelKey, l.code)}</span>
                      {lang === l.code && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* ขวา: Hamburger (mobile only) */}
          <div className="shrink-0 lg:hidden ml-auto">
            <button
              type="button"
              aria-label={t("aria.openMenu", "เปิดเมนู")}
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/90 backdrop-blur-md
                          shadow ring-1 ring-black/10 hover:scale-[1.03] active:scale-[0.98] transition"
            >
              <Menu className="w-6 h-6 text-[#5b4a3e]" />
            </button>
          </div>
        </div>

        {/* ✅ ส่วนที่เพิ่มเข้ามา: แถบไล่ระดับสี (Gradient) เพื่อให้ค่อยๆ จางลง ช่วยลดการทับตัวหนังสือด้านหลัง */}

      </header>

      {/* spacer ให้คอนเทนต์ไม่ชนหัว */}
      <div className="h-20 md:h-28" /> {/* ✅ ปรับ h-20 เป็น h-24/28 เผื่อระยะให้กว้างขึ้นอีกนิดนึงด้วยครับ */}

      {/* Overlay (mobile) */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setOpen(false)}
      />

      {/* Drawer (mobile) */}
      <nav
        className={`fixed top-0 right-0 h-full w-[88%] max-w-[420px] z-[70] bg-[#fffaf2] shadow-2xl ring-1 ring-black/10 
                transition-transform duration-300 ease-out flex flex-col
                ${open ? "translate-x-0" : "translate-x-full"}`}
        aria-hidden={!open}
      >
        {/* หัวเมนู (Fixed Top) */}
        <div className="shrink-0 p-6 flex items-center justify-between border-b border-black/10">
          <div className="flex items-center gap-2">
            <Globe2 className="w-5 h-5 text-[#5b4a3e]" />
            <span className="text-xl font-bold text-[#5b4a3e]">
              {t("menu.title", "Menu")}
            </span>
          </div>
          <button
            type="button"
            aria-label={t("aria.closeMenu", "ปิดเมนู")}
            onClick={() => setOpen(false)}
            className="w-10 h-10 rounded-xl bg-[#5b4a3e] text-white grid place-items-center hover:bg-[#d8653b] transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* เนื้อหาเมนู (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8 pb-32 scroll-pt-4">
          {/* Language */}
          <section className="space-y-2">
            <h3 className="text-sm uppercase font-semibold tracking-wider text-[#a5866e]">
              {t("menu.language", "Language")}
            </h3>
            <div className="relative">
              <select
                value={lang}
                onChange={(e) => handleChangeLang(e.target.value)}
                className="w-full appearance-none px-4 py-3 rounded-xl border border-[#eadfce] bg-white/90 text-[#5b4a3e]
                            font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#d8653b]/40"
              >
                {LANGS.map((l) => (
                  <option key={l.code} value={l.code}>
                    {t(l.labelKey, l.code)}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#5b4a3e]/60">
                ▾
              </div>
            </div>
            <div className="text-sm text-[#5b4a3e]/80 flex items-center gap-2">
              <Check className="w-4 h-4" />
              {t("menu.currentLang", "Current")}:{" "}
              {currentLangItem?.labelKey ? t(currentLangItem.labelKey, lang) : lang}
            </div>
          </section>

          {/* Font size (mobile) */}
          <section className="space-y-3">
            <h3 className="text-sm uppercase font-semibold tracking-wider text-[#a5866e]">
              {t("menu.fontSize", "Font size")}
            </h3>
            <div className="flex items-center justify-start gap-2">
              {["small", "base", "large"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => applyFontScale(k)}
                  className={`px-3 py-2 rounded-lg border font-semibold ${fontScale === k
                    ? "bg-[#5b4a3e] text-white border-[#5b4a3e]"
                    : "bg-white/90 text-[#5b4a3e] border-[#eadfce]"
                    }`}
                >
                  {k === "small" ? "A-" : k === "large" ? "A+" : "A"}
                </button>
              ))}
            </div>
            <div className="text-sm text-[#5b4a3e]/80">
              {t("menu.currentSize", "Current size")}:{" "}
              {fontScale === "small"
                ? "A-"
                : fontScale === "large"
                  ? "A+"
                  : "A"}
            </div>
          </section>

          {/* ลิงก์เมนูหลัก */}
          <section className="pt-2">
            <ul className="space-y-5">
              {[
                {
                  to: "/",
                  label: t("nav.home", "หน้าหลัก"),
                },
                {
                  to: "/books",
                  label: t("nav.books", "ทรัพยากรสารสนเทศ"),
                },
                {
                  to: "/memory-of-the-world",
                  label: (
                    <span className="flex flex-col leading-tight">
                      <span>{t("nav.mow_1", "มรดกความทรงจำ")}</span>
                      <span>{t("nav.mow_2", "แห่งโลก")}</span>
                    </span>
                  ),
                },
                {
                  to: "/world-heritage",
                  label: t("nav.world_heritage", "มรดกโลก"),
                },
                {
                  to: "/articles",
                  label: t("nav.articles", "องค์ความรู้"),
                },
                {
                  to: "/games",
                  label: t("nav.games", "เกมล้านนา"),
                },
                {
                  to: "/facebook/activities",
                  label: t("nav.facebook", "ข่าว Facebook"),
                },
              ].map((m) => (
                <li key={m.to}>
                  <NavLink
                    to={m.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block text-2xl font-bold tracking-tight transition ${isActive
                        ? "text-[#d8653b]"
                        : "text-[#5b4a3e] hover:text-[#d8653b]"
                      }`
                    }
                  >
                    {m.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </nav>
    </>
  );
}

// ห่อด้วย memo ลด re-render ถ้า props centerSlot ไม่เปลี่ยน
export default memo(SiteHeaderInner);